import { useId } from 'react';
import {
  ArrowRightIcon,
  BracesIcon,
  LockIcon,
  MoreHorizontalIcon,
  PencilIcon,
  TextIcon,
  TrashIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HealthChip } from '@/features/connections/components/HealthChip';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_PHONE_END,
  LIST_ROW_PHONE_GRID,
  LIST_ROW_PHONE_NAME,
  LIST_ROW_PHONE_SECOND,
  LIST_ROW_PHONE_TEXT,
  LIST_ROW_SECONDARY,
  LIST_ROW_TRAILING,
  LIST_ROWS,
} from '@/shared/components/listRow';
import { SectionHeading } from '@/shared/components/SectionHeading';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import type { SecretGroup } from '../secretFilters';
import { SecretValueCell } from './SecretValueCell';

export interface SecretsListProps {
  workspaceId: string;
  groups: SecretGroup[];
  /** Keys to pulse after a connection saved them. */
  highlight: ReadonlySet<string>;
  onEdit: (entry: WorkspaceSecretView) => void;
  onDescribe: (entry: WorkspaceSecretView) => void;
  onDelete: (entry: WorkspaceSecretView) => void;
  /** Opens the connection a managed group came from. */
  onOpenConnection: (group: SecretGroup) => void;
}

/** Readability follows the *kind*, never whether `value` happens to be null.
 *
 * Deriving it from the data would render a variable whose value is genuinely
 * empty as a secret, and — worse — would let a kind added later inherit
 * "readable" from a null it happened to send. */
function isReadable(entry: WorkspaceSecretView): boolean {
  return entry.kind === 'variable';
}

const ROW = `${LIST_ROW} ${LIST_ROW_PHONE_GRID} hover:bg-muted/50 focus-within:bg-muted/50 transition-colors`;
const HIGHLIGHT = 'bg-primary/5 ring-primary/40 ring-1';

/**
 * The workspace's entries as one list (spec 229 FR-011): custom and managed
 * rows in the same shape, in groups under the shared uppercase heading —
 * "Custom", then "Managed by <connection>" per connection. One group of
 * custom entries draws no heading at all, which keeps the common case as
 * plain as it was.
 *
 * The old subgrid aligned the value cells down the page; the shared row keeps
 * the cell at a fixed width instead, so the columns still line up without a
 * grid. A managed row offers only the way into its connection: the entry
 * changes through the connection, and the backend refuses anything else
 * (`SECRET_MANAGED`).
 *
 * The row menu offers no rename and no kind change, because neither exists.
 * Their absence is how the page answers "can I rename this?" without the person
 * having to try it (FR-011).
 */
export function SecretsList({
  workspaceId,
  groups,
  highlight,
  onEdit,
  onDescribe,
  onDelete,
  onOpenConnection,
}: SecretsListProps) {
  const idBase = useId();
  const rows = (group: SecretGroup) => (
    <ul aria-label={`${group.title} entries`} className={LIST_ROWS}>
      {group.entries.map((entry) =>
        entry.managed_by ? (
          <ManagedRow
            key={entry.key}
            entry={entry}
            group={group}
            highlighted={highlight.has(entry.key)}
            onOpenConnection={() => {
              onOpenConnection(group);
            }}
          />
        ) : (
          <CustomRow
            key={entry.key}
            entry={entry}
            highlighted={highlight.has(entry.key)}
            onEdit={onEdit}
            onDescribe={onDescribe}
            onDelete={onDelete}
          />
        ),
      )}
    </ul>
  );

  if (groups.length === 1 && groups[0]?.key === 'custom') {
    return rows(groups[0]);
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group, index) => {
        const headingId = `${idBase}-${String(index)}`;
        const managed = group.key !== 'custom';
        return (
          <section
            key={group.key}
            aria-labelledby={headingId}
            className="flex flex-col gap-1"
          >
            <SectionHeading
              as="h3"
              id={headingId}
              action={
                managed ? (
                  <span className="flex items-center gap-2">
                    {group.connection ? (
                      <HealthChip
                        workspaceId={workspaceId}
                        connection={group.connection}
                      />
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onOpenConnection(group);
                      }}
                    >
                      Open connection
                      <ArrowRightIcon aria-hidden="true" />
                    </Button>
                  </span>
                ) : undefined
              }
            >
              {managed
                ? `Managed by ${group.title}${group.disconnected ? ' (disconnected)' : ''}`
                : 'Custom'}
            </SectionHeading>
            {rows(group)}
          </section>
        );
      })}
    </div>
  );
}

function KindIcon({ entry }: { entry: WorkspaceSecretView }) {
  return isReadable(entry) ? (
    <BracesIcon
      aria-hidden="true"
      data-testid="kind-icon-variable"
      className="text-muted-foreground size-5 shrink-0"
      strokeWidth={1.5}
    />
  ) : (
    <LockIcon
      aria-hidden="true"
      data-testid="kind-icon-secret"
      className="text-muted-foreground size-5 shrink-0"
      strokeWidth={1.5}
    />
  );
}

/** The key, with the mark only a secret earns beside it, and its description. */
function KeyCell({ entry }: { entry: WorkspaceSecretView }) {
  return (
    <span
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-0.5',
        LIST_ROW_PHONE_TEXT,
      )}
    >
      <span
        className={cn('flex min-w-0 items-center gap-2', LIST_ROW_PHONE_NAME)}
      >
        <span className="truncate font-mono text-sm">{entry.key}</span>
        {/* Only the secret is badged: a badge on every row is a column
            wearing a chip's clothes. A tint of `--primary`, not `secondary`,
            so it has an edge on the card without shouting over the page's
            one real button. */}
        {isReadable(entry) ? null : (
          <Badge
            variant="secondary"
            className="bg-primary/10 text-primary shrink-0 tracking-wide"
          >
            Secret
          </Badge>
        )}
      </span>
      {/* Only when there is one: reserving the line for undocumented entries
          would pad the list with nothing. */}
      {entry.description ? (
        <span
          className={cn(
            LIST_ROW_SECONDARY,
            'max-sm:whitespace-normal',
            LIST_ROW_PHONE_SECOND,
          )}
        >
          {entry.description}
        </span>
      ) : null}
    </span>
  );
}

function CustomRow({
  entry,
  highlighted,
  onEdit,
  onDescribe,
  onDelete,
}: {
  entry: WorkspaceSecretView;
  highlighted: boolean;
  onEdit: (entry: WorkspaceSecretView) => void;
  onDescribe: (entry: WorkspaceSecretView) => void;
  onDelete: (entry: WorkspaceSecretView) => void;
}) {
  return (
    <li className={cn(ROW, highlighted && HIGHLIGHT)}>
      <KindIcon entry={entry} />
      <KeyCell entry={entry} />
      <SecretValueCell entry={entry} className="hidden md:flex md:w-72" />
      <span className={cn(LIST_ROW_TRAILING, LIST_ROW_PHONE_END)}>
        <span
          className="text-muted-foreground hidden text-xs whitespace-nowrap tabular-nums md:block"
          title={formatExactTime(entry.updated_at)}
        >
          Custom · updated <span>{formatRelativeTime(entry.updated_at)}</span>
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                className="touch-target shrink-0"
              >
                <MoreHorizontalIcon aria-hidden="true" />
                <span className="sr-only">Actions for {entry.key}</span>
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            {/* Above "Replace value", and separate from it: editing a note and
                rotating a credential are different acts, one reversible and
                one not for a secret, so they are never the same form. */}
            <DropdownMenuItem
              onClick={() => {
                onDescribe(entry);
              }}
            >
              <TextIcon aria-hidden="true" />
              {entry.description ? 'Edit description' : 'Add description'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                onEdit(entry);
              }}
            >
              <PencilIcon aria-hidden="true" />
              Replace value
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                onDelete(entry);
              }}
            >
              <TrashIcon aria-hidden="true" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </li>
  );
}

function ManagedRow({
  entry,
  group,
  highlighted,
  onOpenConnection,
}: {
  entry: WorkspaceSecretView;
  group: SecretGroup;
  highlighted: boolean;
  onOpenConnection: () => void;
}) {
  return (
    <li className={cn(ROW, highlighted && HIGHLIGHT)}>
      <KindIcon entry={entry} />
      <KeyCell entry={entry} />
      {/* A variable's value is what tells the reader which host the token is
          for; a secret shows a fixed mask, since nothing of it is readable. */}
      <span
        data-testid={isReadable(entry) ? undefined : 'value-mask'}
        className="text-muted-foreground hidden truncate font-mono text-xs md:block md:w-72"
      >
        {isReadable(entry) ? (entry.value ?? '') : '••••••'}
      </span>
      <span className={cn(LIST_ROW_TRAILING, LIST_ROW_PHONE_END)}>
        <span className="text-muted-foreground hidden text-xs whitespace-nowrap md:block">
          From the {group.title} connection
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                className="touch-target shrink-0"
              >
                <MoreHorizontalIcon aria-hidden="true" />
                <span className="sr-only">Actions for {entry.key}</span>
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpenConnection}>
              <ArrowRightIcon aria-hidden="true" />
              Open connection
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </li>
  );
}
